import { useCallback, useEffect, useRef, useState } from "react";
import { AudioPlayer, Waveform } from "@xinghai/ui";
import type { AudioPlayerHandle } from "@xinghai/ui";
import { computePeaks } from "../../../lib/audio/peaks";

/** Bar count that stays legible in a ~340px sticky column. */
const BAR_COUNT = 240;

export interface AudioPanelProps {
  /** `GET recordings/:id/audio` — auth is the session cookie. */
  src: string;
  /** Bind Space / J / K / R to the document. @default true */
  keyboard?: boolean;
  /** Phone layout: shorter waveform, no keyboard legend. */
  compact?: boolean;
  /** 快速模式 — start playing as soon as the clip is mounted. */
  autoPlay?: boolean;
  /**
   * docs/UX.md §2.3 — approximate per-character positions under the
   * waveform. The split is even (no forced alignment): it is a navigation aid,
   * never a label. Clicking one seeks there.
   */
  charMarkers?: readonly string[];
  /** Index of the character currently being judged. */
  activeMarker?: number;
  onPickMarker?: (index: number) => void;
  label?: string;
  className?: string;
}

/** True while the user is typing — the shortcuts must not steal those keys. */
function inTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return el.isContentEditable === true;
}

/**
 * The left column of the workbench and of the arbitration panel: one
 * `<audio>` element driving both the transport and the waveform playhead.
 * docs/UX.md 7 — Space 播放/暂停, J/K ∓1 s, R 重放.
 */
export function AudioPanel({
  src,
  keyboard = true,
  compact = false,
  autoPlay = false,
  charMarkers,
  activeMarker,
  onPickMarker,
  label = "录音",
  className,
}: AudioPanelProps) {
  const player = useRef<AudioPlayerHandle>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);

  // Decode once for the waveform. Best-effort: no AudioContext (jsdom, older
  // Safari) or a failed decode simply leaves the bars empty.
  useEffect(() => {
    let alive = true;
    setPeaks(null);
    const Ctor =
      typeof window === "undefined"
        ? undefined
        : (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor || typeof fetch !== "function") return;
    void (async () => {
      let ctx: AudioContext | null = null;
      try {
        const res = await fetch(src, { credentials: "include" });
        if (!res.ok) return;
        const buf = await res.arrayBuffer();
        ctx = new Ctor();
        const decoded = await ctx.decodeAudioData(buf);
        if (!alive) return;
        setPeaks(computePeaks(decoded.getChannelData(0), BAR_COUNT));
      } catch {
        /* waveform is decoration; the transport still works */
      } finally {
        void ctx?.close?.();
      }
    })();
    return () => {
      alive = false;
    };
  }, [src]);

  const seek = useCallback((s: number) => player.current?.seek(s), []);

  // 快速模式: the clip starts on its own. Browsers may block this without a
  // prior gesture — the transport stays usable either way.
  useEffect(() => {
    if (!autoPlay) return;
    player.current?.seek(0);
    player.current?.play();
  }, [autoPlay, src]);

  useEffect(() => {
    if (!keyboard || typeof document === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inTextEntry(e.target)) return;
      const p = player.current;
      if (!p) return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        p.toggle();
      } else if (e.key === "j" || e.key === "J") {
        e.preventDefault();
        p.nudge(-1);
      } else if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        p.nudge(1);
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        p.seek(0);
        p.play();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [keyboard]);

  return (
    <section
      className={["xz-audiopanel", compact && "xz-audiopanel--compact", className]
        .filter(Boolean)
        .join(" ")}
    >
      <AudioPlayer
        ref={player}
        src={src}
        label={label}
        size={compact ? "sm" : "md"}
        onTime={setTime}
        onDuration={setDuration}
      />
      {!compact && (
        <Waveform
          peaks={peaks}
          durationSec={duration}
          currentSec={time}
          onSeek={seek}
          height={88}
          label="波形，点击定位"
        />
      )}
      {!compact && charMarkers && charMarkers.length > 0 && (
        <div className="xz-audiopanel__marks" role="group" aria-label="按字定位（位置为大致估算）">
          {charMarkers.map((label_, i) => (
            <button
              key={`${label_}-${i}`}
              type="button"
              className={["xz-audiopanel__mark", i === activeMarker && "is-active"]
                .filter(Boolean)
                .join(" ")}
              aria-label={`跳到第 ${i + 1} 字 ${label_}`}
              aria-current={i === activeMarker ? "true" : undefined}
              onClick={() => {
                const d = player.current?.duration() ?? 0;
                if (d > 0) player.current?.seek((i / charMarkers.length) * d);
                onPickMarker?.(i);
              }}
            >
              {label_}
            </button>
          ))}
        </div>
      )}
      {!compact && keyboard && (
        <p className="gz-caption xz-audiopanel__keys">
          空格 播放/暂停 · J 后退 1 秒 · K 前进 1 秒 · R 重放
        </p>
      )}
    </section>
  );
}
